<template>
    <main role="main" class="py-4">
        <b-container fluid="xxl">
            <div class="d-flex flex-row align-items-center justify-content-between mb-4">
                <h4 class="m-0">{{ $t("meetingDetailReport") }}</h4>
                <div>
                    <!-- <b-button variant="outline-secondary" @click="onClickReloadBtn">
              <b-icon icon="arrow-clockwise"></b-icon> {{ $t("reload") }}
            </b-button> -->
                    <b-button variant="outline-success" :disabled="!totalItems || isExportingToExcel"
                        @click="debouncedonClickExportToExcel()">
                        <b-icon-download class="mr-2"></b-icon-download> {{ $t("export") }}
                    </b-button>
                </div>
            </div>

            <b-form class="d-flex flex-row align-items-center mb-3" @submit.prevent="onSubmitSearch">
                <b-form-input class="mr-3" type="search" style="width: 280px;" minlength="2" maxlength="200"
                    v-model="searchText" :placeholder="$t('meetingDetailReportSearchPlaceholder')" />
                <b-form-select v-if="isUserAdmin()" class="mr-3" :options="searchAreaOptions" v-model="searchAreaId"
                    style="width: 280px;"></b-form-select>
                <div class="flex-grow-1 d-flex flex-row align-items-center mr-3">
                    <div class="flex-shrink-0 mr-3">{{ $t("fromDate") }}</div>
                    <b-form-datepicker v-model="startDate" value-as-date required :max="endDate ? endDate : new Date()"
                        no-flip hide-header label-no-date-selected="DD/MM/YYYY" locale="vi"
                        :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                        start-weekday="1"></b-form-datepicker>
                </div>
                <div class="flex-grow-1 d-flex flex-row align-items-center mr-3">
                    <div class="flex-shrink-0 mr-3">{{ $t("toDate") }}</div>
                    <b-form-datepicker v-model="endDate" value-as-date required :min="startDate ? startDate : new Date()"
                        no-flip hide-header label-no-date-selected="DD/MM/YYYY" locale="vi"
                        :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                        start-weekday="1"></b-form-datepicker>
                </div>
                <b-button type="submit" variant="primary">{{ $t("search") }}</b-button>
            </b-form>

            <b-table ref="table" :items="tableItemProvider" hover primary-key="id" :fields="tableFields"
                :busy="isLoading" show-empty :per-page="itemsPerPage" :current-page="currentPage">
                <template v-slot:table-busy>
                    <div class="text-center text-secondary py-4">{{ $t("loadingData") }}</div>
                </template>
                <template v-slot:empty>
                    <div class="text-center text-secondary py-4">{{ $t("noData") }}</div>
                </template>
            </b-table>

            <div class="d-flex flex-row justify-content-end">
                <div class="d-flex flex-row align-items-center">
                <div v-if="totalItems" class="text-right flex-shrink-0 mr-3">{{ $t("resultCount", { count: totalItems }) }}</div>
                <b-pagination class="my-0 mr-3" v-model="currentPage" :total-rows="totalItems" :per-page="itemsPerPage"></b-pagination>
                <b-form-select v-model="itemsPerPage" :options="itemsPerPageSelections"></b-form-select>
                </div>
            </div>


        </b-container>
    </main>
</template>

<style lang="scss" scoped></style>

<script lang="ts">
import { Component, Ref, Vue } from 'vue-property-decorator';
import _ from "lodash";
import moment from "moment";
import BaseComponent from "@/components/BaseComponent";
import { BvTableFieldArray } from "bootstrap-vue";
import { BTable, BvTableCtxObject, BvTableProviderPromiseResult } from "bootstrap-vue/src/components/table";
import * as reportService from "@/service/reportService";
import * as areaService from "@/service/areaService";
import { BounceTimes } from '../enums/TimesNumbers';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { SearchRequestManager } from "@/utils/search-request-manager";

@Component
export default class MeetingDetailReport extends BaseComponent {
    searchManager = new SearchRequestManager();

    searchText = "";

    searchAreaId: number = null;
    searchAreaOptions = [];

    startDate: Date = null;
    endDate: Date = null;
    formErrorMsg = "";
    isLoading = false;

    @Ref("table") table: BTable;
    tableFields: BvTableFieldArray = [
        {
            key: "id",
            label: "ID"
        },
        {
            key: "name",
            label: this.$t("meetingName").toString(),
        },
        , {
            key: "createdByName",
            label: this.$t("creator").toString(),
        },
        {
            key: "createTime",
            formatter: this.formatter,
            label: this.$t("createdDate").toString(),
        },

    ];

    isExportingToExcel = false;

    itemsPerPageSelections = [
        { value: 20, text: "20 " + this.$t("itemsPerPage").toString() },
        { value: 50, text: "50 " + this.$t("itemsPerPage").toString() },
        { value: 100, text: "100 " + this.$t("itemsPerPage").toString() },
        { value: 200, text: "200 " + this.$t("itemsPerPage").toString() },
    ];
    itemsPerPage = 20;
    currentPage = 1;
    totalItems = 0;


    mounted() {
        // this.startDate = moment().subtract(30, "days").startOf("day").toDate();
        // this.endDate = moment().endOf("day").toDate();
        this.fetchAreas();
    }

    formatter(value, key, index) {
        value = new Date(value);
        return value.toLocaleDateString('en-GB');
    }

    async fetchAreas() {
        try {
            const areas = await areaService.getAll();
            this.searchAreaOptions = areas.map(a => ({ value: a.id, text: a.name }));
            this.searchAreaOptions.unshift({ value: null, text: this.$t("all").toString() });

        } catch (e) {
            this.toastError(e);
        }
    }

    onClickReloadBtn() {
        this.searchManager.reset();
        this.searchText = "";
        this.formErrorMsg = "";
        this.itemsPerPage = 20;
        this.currentPage = 1;
        this.totalItems = 0;

        this.table.refresh();
    }

    //
    debouncedonClickSearchBtn = _.debounce(() => {
        this.onClickSearchBtn();
    }, BounceTimes.BOUNCETIMES);

    onClickSearchBtn() {
        this.table.refresh();
    }

    onSubmitSearch() {
        this.searchManager.onSubmit({
            debounceFn: this.debouncedonClickSearchBtn,
            resetPage: () => {
                this.currentPage = 1;
            },
            execute: () => {
                this.table.refresh();
            },
        });
    }

    async tableItemProvider(ctx: BvTableCtxObject): Promise<BvTableProviderPromiseResult> {
        const currentRequestId = this.searchManager.nextId();
        
        let startDateString = "";
        if(this.startDate != null){
            const startDate = new Date(this.startDate);
            startDate.setDate(startDate.getDate() + 1);
            startDate.setUTCHours(0, 0, 0, 0);
            startDateString = startDate.toISOString();

        }

        let endDateString = "";
        if(this.endDate != null){
            const currentEndDate = new Date(this.endDate);
            currentEndDate.setDate(currentEndDate.getDate() + 2);
            currentEndDate.setUTCHours(0, 0, 0, 0);
            endDateString = currentEndDate.toISOString();
        }
        try {
            const customerListRes = await reportService.getMeetingListByCustomerUser({
                fromDate: this.startDate ? startDateString : null,
                toDate: this.endDate ? endDateString : null,
                page: ctx.currentPage,
                pageSize: this.itemsPerPage,

                q: this.searchText?.trim(),
                areaId: this.searchAreaId
            });

            if (!this.searchManager.isLatest(currentRequestId)) {
                return [];
            }

            this.totalItems = customerListRes.total;
            return customerListRes.data;

        } catch (e) {
            if (this.searchManager.isLatest(currentRequestId)) {
                this.toastError(e);
            }
            return [];
        }
    }

    //
    debouncedonClickExportToExcel  = _.debounce(() => {
        this.onClickExportToExcel ();
    }, BounceTimes.BOUNCETIMES);

    async onClickExportToExcel() {
        try {
            this.isExportingToExcel = true;
            const pageSize = 500;
            const customerListRes = await reportService.getExportExcelByCustomerUser({
                fromDate: this.startDate ? this.startDate.toISOString() : null,
                toDate: this.endDate ? this.endDate.toISOString() : null,
                page: 1,
                pageSize,
                q: this.searchText?.trim(),
            });

            const workbook = XLSX.read(customerListRes, {
                type: 'string',
                raw: true,
                codepage: 65001,
            });

            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const newWorkbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(newWorkbook, worksheet);

            const excelBuffer = XLSX.write(newWorkbook, {
                bookType: 'xlsx',
                type: 'array',
            });

            const blob = new Blob([excelBuffer], {
                type: 'application/octet-stream',
            });
            const fileName = `ThongKeChiTiet_CuocHop.xlsx`;
            saveAs(blob, fileName);
        } catch (e) {
            console.error(e);
            this.toastError(e);

        } finally {
            this.isExportingToExcel = false;
        }
    }

}
</script>